import { useEffect, useMemo, useState, Fragment } from "react";
import PropTypes from "prop-types";
import DarkModeToggle from "./DarkMode/index.jsx";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";
import {AiOutlineFullscreen, AiOutlineFullscreenExit, AiOutlineSetting} from "react-icons/ai";
import {
    getFullscreenActivationContext,
    getFullscreenToggleContext,
} from "../providers/FullScreenProvider.jsx";
import { getThemeContext } from "../providers/ThemeProvider.jsx";
import { useAuthContext } from "../providers/AuthProvider";
import { useCartContext } from "../providers/CartProvider.jsx";
import LogoNavbar from "./LogoNavbar.jsx";
import DropdownUser2 from "./DropdownUser/DropdownUser2.jsx";
import classNames from "classnames";
import { PRODUCTS } from "../../products/registry";
import { mdiCartOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { ShoppingCart } from "lucide-react";
import { Menu, Transition } from "@headlessui/react";
import CommerceItemImage from "../store/CommerceItemImage.jsx";


const fmt = (n) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);

// ── Cart mini-dropdown ─────────────────────────────────────────────────────────
const CartDropdown = ({ cartItems, fetchCart }) => {
    const navigate = useNavigate();
    const [refreshing, setRefreshing] = useState(false);

    const totalUnits = (cartItems ?? []).reduce((acc, i) => acc + (i.quantity || 0), 0);
    const totalPrice = (cartItems ?? []).reduce(
        (acc, i) => acc + (parseFloat(i.product_price) || 0) * (i.quantity || 0), 0
    );
    const isEmpty = (cartItems ?? []).length === 0;

    const refreshCart = async () => {
        if (!fetchCart || refreshing) return;
        setRefreshing(true);
        try {
            await fetchCart();
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <Menu as="div" className="relative">
            <Menu.Button
                className="relative btn btn-ghost btn-circle"
                onClick={refreshCart}
                aria-label="Abrir carrito"
            >
                <Icon path={mdiCartOutline} size={1} />
                {totalUnits > 0 && (
                    <motion.span
                        key={totalUnits}
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1
                                   rounded-full bg-brand-red text-white text-[10px] font-bold
                                   flex items-center justify-center leading-none"
                    >
                        {totalUnits > 99 ? '99+' : totalUnits}
                    </motion.span>
                )}
            </Menu.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 scale-95 translate-y-1"
                enterTo="opacity-100 scale-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 scale-100 translate-y-0"
                leaveTo="opacity-0 scale-95 translate-y-1"
            >
                <Menu.Items
                    className="absolute right-0 mt-2 w-80 origin-top-right z-50
                               bg-base-100 rounded-2xl shadow-2xl shadow-black/30
                               ring-1 ring-black/5 dark:ring-white/10
                               focus:outline-none overflow-hidden"
                >
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-base-300 flex items-center justify-between">
                        <span className="text-sm font-semibold text-base-content">
                            Mi carrito
                        </span>
                        {!isEmpty && (
                            <span className="text-xs text-base-content/50">
                                {totalUnits} {totalUnits === 1 ? 'artículo' : 'artículos'}
                            </span>
                        )}
                    </div>

                    {/* Items list */}
                    <div className="max-h-72 overflow-y-auto">
                        {isEmpty ? (
                            <div className="flex flex-col items-center justify-center gap-2 py-8 text-base-content/40">
                                <ShoppingCart size={32} strokeWidth={1.5} />
                                <p className="text-sm">Tu carrito está vacío</p>
                            </div>
                        ) : (
                            <div className="p-2 flex flex-col gap-1">
                                {cartItems.map((item, idx) => {
                                    return (
                                        <div
                                            key={`${item.id}_${idx}`}
                                            className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-base-200 transition-colors"
                                        >
                                            {/* Image */}
                                            <CommerceItemImage
                                                item={item}
                                                customKeyring={item.item_type === 'digital' ? item.digital_package_detail : undefined}
                                                alt={item.product_name}
                                                className="w-11 h-11"
                                                show3dOnHover
                                            />
                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-base-content truncate">
                                                    {item.product_name}
                                                </p>
                                                <p className="text-xs text-base-content/50">
                                                    {item.quantity} × {fmt(item.product_price)}
                                                </p>
                                            </div>
                                            {/* Line total */}
                                            <p className="text-sm font-semibold text-base-content flex-shrink-0">
                                                {fmt((parseFloat(item.product_price) || 0) * item.quantity)}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {!isEmpty && (
                        <div className="border-t border-base-300 p-3 flex flex-col gap-2">
                            <div className="flex justify-between items-center px-1">
                                <span className="text-sm text-base-content/70">Total</span>
                                <span className="text-base font-bold text-base-content">
                                    {fmt(totalPrice)}
                                </span>
                            </div>
                            <Menu.Item>
                                {({ close }) => (
                                    <button
                                        onClick={() => { close(); navigate('/cart'); }}
                                        className="w-full py-2 rounded-xl text-sm font-semibold
                                                   bg-brand-dark text-white dark:bg-primary dark:text-primary-content
                                                   hover:brightness-90 transition-all duration-200
                                                   relative overflow-hidden group"
                                    >
                                        Ver carrito completo
                                        <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-brand-red
                                                         transition-[width] duration-300 group-hover:w-full dark:hidden" />
                                    </button>
                                )}
                            </Menu.Item>
                        </div>
                    )}
                </Menu.Items>
            </Transition>
        </Menu>
    );
};

CartDropdown.propTypes = {
    cartItems: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        item_type: PropTypes.string,
        product_name: PropTypes.string,
        product_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        quantity: PropTypes.number,
        digital_package_detail: PropTypes.object,
    })),
    fetchCart: PropTypes.func,
};

// ── Main navbar ────────────────────────────────────────────────────────────────
const HomeNavbar = () => {
    const fullscreenActivationContext = getFullscreenActivationContext();
    const fullscreenToggleContext = getFullscreenToggleContext();
    const themeContext = getThemeContext();
    const authContext = useAuthContext();

    // Importante: aquí accedemos al contexto del carrito
    const { cartItems, fetchCart } = useCartContext();

    const [hidden, setHidden] = useState(false);
    const location = useLocation();

    // Full-screen routes hide the navbar. The editors come from the product
    // registry rather than a hardcoded list, so adding a product does not mean
    // remembering to edit this too.
    const fullScreenPaths = useMemo(
        () => [
            ...Object.values(PRODUCTS).map((product) => product.routeBase),
            "/viewer",
            "/public-viewer",
        ],
        [],
    );

    useEffect(() => {
        setHidden(fullScreenPaths.some((path) => location.pathname.startsWith(path)));
    }, [location, fullScreenPaths]);

    const variants = {
        visible: { opacity: 1, y: 0 },
        hidden: { opacity: 0, y: -25 },
    };

    // Función para verificar si el enlace está activo
    const isActiveLink = (path) => {
        return location.pathname === path;
    };


    return (
        <motion.div
            variants={variants}
            animate={hidden ? "hidden" : "visible"}
            transition={{ ease: [0.1, 0.25, 0.3, 1], duration: 0.6 }}
            // className=""
            className={` align-middle w-full z-50 ${ hidden ? "min-h-0" : "min-h-[4rem]"} `}
        >
            <div
                className={classNames(
                    "border-b-[1px] border-gray-500 bg-gray-400 dark:bg-base-100 text-neutral dark:text-neutral navbar fixed top-0 z-10 w-full shadow-md backdrop-blur bg-opacity-90",
                    hidden ? "hidden" : "visible"
                )}
            >
                {/* Navbar Start */}
                <div className="navbar-start text-black dark:text-white">


                    <Menu as="div" className="relative inline-block text-left z-50 lg:hidden">
                        <Menu.Button className="btn btn-ghost">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 6h16M4 12h8m-8 6h16"
                                />
                            </svg>
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="transform opacity-0 scale-y-95"
                            enterTo="transform opacity-100 scale-y-100"
                            leave="transition ease-in duration-150"
                            leaveFrom="transform opacity-100 scale-y-100"
                            leaveTo="transform opacity-0 scale-y-95"
                        >
                            <Menu.Items className="absolute mt-2 w-52 origin-top-right shadow-lg bg-base-100 rounded-box focus:outline-none">
                                <div className="p-2">
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/"
                                                className={`${
                                                    active ? 'bg-gray-200' : ''
                                                } block px-4 py-2 text-sm`}
                                            >
                                                Home
                                            </Link>
                                        )}
                                    </Menu.Item>
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/about"
                                                className={`${
                                                    active ? 'bg-gray-200' : ''
                                                } block px-4 py-2 text-sm`}
                                            >
                                                About
                                            </Link>
                                        )}
                                    </Menu.Item>
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/contact"
                                                className={`${
                                                    active ? 'bg-gray-200' : ''
                                                } block px-4 py-2 text-sm`}
                                            >
                                                Contacto
                                            </Link>
                                        )}
                                    </Menu.Item>
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/store"
                                                className={`${
                                                    active ? 'bg-gray-200' : ''
                                                } block px-4 py-2 text-sm`}
                                            >
                                                Tienda
                                            </Link>
                                        )}
                                    </Menu.Item>
                                    {authContext.role === 'admin' && (
                                        <Menu.Item>
                                            {({ active }) => (
                                                <Link
                                                    to="/admin_store"
                                                    className={`${
                                                        active ? 'bg-gray-200' : ''
                                                    } block px-4 py-2 text-sm`}
                                                >
                                                    AdminStore
                                                </Link>
                                            )}
                                        </Menu.Item>
                                    )}
                                    {authContext.isAuth && (
                                        <Menu.Item>
                                            {({ active }) => (
                                                <Link
                                                    to="/customizers"
                                                    className={`${
                                                        active ? 'bg-gray-200' : ''
                                                    } block px-4 py-2 text-sm`}
                                                >
                                                    Customiz3r
                                                </Link>
                                            )}
                                        </Menu.Item>
                                    )}
                                </div>
                            </Menu.Items>
                        </Transition>
                    </Menu >

                    <span className="lg:ml-12">
                        <LogoNavbar themeContext={themeContext} />
                    </span>
                </div>

                {/* Navbar Center */}
                <div className="navbar-center hidden lg:flex">
                    <ul className="menu menu-horizontal menu-sm px-1 text-black dark:text-white gap-2">
                        <li>
                            <Link className={isActiveLink("/") ? "font-bold focus:text-white dark:focus:text-white focus:bg-none" : ""} to="/">Home</Link>
                        </li>

                        <li>
                            <Link className={isActiveLink("/about") ? "font-bold focus:text-white dark:focus:text-white focus:bg-none" : ""} to="/about">About</Link>
                        </li>

                        <li>
                            <Link className={isActiveLink("/contact") ? "font-bold focus:text-white dark:focus:text-white focus:bg-none" : ""} to="/contact">Contacto</Link>
                        </li>

                        <li>
                            <Link className={isActiveLink("/store") ? "font-bold focus:text-white dark:focus:text-white focus:bg-none" : ""} to="/store">Tienda</Link>
                        </li>

                        {authContext.isAuth && (
                            <li><Link to="/customizers">Customiz3rs</Link></li>
                        )}

                    </ul>
                </div>

                {/* Navbar End */}
                <div className="navbar-end flex items-center gap-4 text-black dark:text-white">

                    {/* ── Cart dropdown ───────────────────────────────────── */}
                    <CartDropdown cartItems={cartItems} fetchCart={fetchCart} />







                    {authContext.isLoading ? (
                        <span className="loading loading-spinner loading-sm text-primary" />
                    ) : authContext.isAuth ? (
                        <DropdownUser2 />
                    ) : (
                        <Link className="btn btn-ghost" to="/login">Login</Link>
                    )}


                    <Menu as="div" className="relative inline-block text-left">
                        <Menu.Button className="btn btn-ghost btn-circle hover:btn-primary transition-colors duration-200 group">
                            <AiOutlineSetting
                                size={20}
                                className="group-hover:rotate-45 transition-transform duration-300"
                            />
                        </Menu.Button>

                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="transform opacity-0 scale-95 -translate-y-1"
                            enterTo="transform opacity-100 scale-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="transform opacity-100 scale-100 translate-y-0"
                            leaveTo="transform opacity-0 scale-95 -translate-y-1"
                        >
                            <Menu.Items className="absolute mt-3 w-64 origin-top-right right-0 bg-base-100 rounded-xl  shadow-xl shadow-black   ring-1 ring-black ring-opacity-5 focus:outline-none z-50 overflow-hidden backdrop-blur-sm">
                                {/* Header del menú */}
                                <div className="px-4 py-3 bg-gradient-to-r from-primary/10 to-secondary/10 border-b border-base-300">
                                    <h3 className="text-sm font-semibold text-base-content/80">Configuración</h3>
                                </div>

                                {/* Contenido del menú */}
                                <div className="p-3 space-y-2">
                                    {/* Fullscreen Toggle */}
                                    <Menu.Item>
                                        {({ active, close }) => (
                                            <button
                                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                                                    active
                                                        ? 'bg-primary text-primary-content shadow-md transform scale-[1.02]'
                                                        : 'text-base-content hover:bg-base-200'
                                                }`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    fullscreenToggleContext();
                                                    close();
                                                }}
                                            >
                                                <div className={`p-1.5 rounded-lg ${
                                                    active ? 'bg-primary-content/20' : 'bg-base-300'
                                                }`}>
                                                    {fullscreenActivationContext ? (
                                                        <AiOutlineFullscreenExit size={16} />
                                                    ) : (
                                                        <AiOutlineFullscreen size={16} />
                                                    )}
                                                </div>
                                                <span className="flex-1 text-left">
                                        {fullscreenActivationContext ? 'Salir de pantalla completa' : 'Pantalla completa'}
                                    </span>
                                            </button>
                                        )}
                                    </Menu.Item>

                                    {/* Dark Mode Toggle */}
                                    <Menu.Item>
                                        {({ active }) => (
                                            <div
                                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                                                    active
                                                        ? 'bg-secondary text-secondary-content shadow-md transform scale-[1.02]'
                                                        : 'text-base-content hover:bg-base-200'
                                                }`}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div className={`p-1.5 rounded-lg ${
                                                    active ? 'bg-secondary-content/20' : 'bg-base-300'
                                                }`}>
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                                                        />
                                                    </svg>
                                                </div>
                                                <span className="flex-1 text-left">Modo oscuro</span>
                                                <div className="ml-auto">
                                                    <DarkModeToggle />
                                                </div>
                                            </div>
                                        )}
                                    </Menu.Item>

                                    {/* Separador */}
                                    <div className="border-t border-base-300 my-2"></div>

                                    {/* Opciones adicionales */}
                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                                                    active
                                                        ? 'bg-info text-info-content shadow-md transform scale-[1.02]'
                                                        : 'text-base-content hover:bg-base-200'
                                                }`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    // Aquí puedes añadir más funcionalidad
                                                    console.log('Configuración avanzada');
                                                }}
                                            >
                                                <div className={`p-1.5 rounded-lg ${
                                                    active ? 'bg-info-content/20' : 'bg-base-300'
                                                }`}>
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                    </svg>
                                                </div>
                                                <span className="flex-1 text-left">Configuración avanzada</span>
                                            </button>
                                        )}
                                    </Menu.Item>

                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                                                    active
                                                        ? 'bg-success text-success-content shadow-md transform scale-[1.02]'
                                                        : 'text-base-content hover:bg-base-200'
                                                }`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    // Funcionalidad de ayuda
                                                    console.log('Mostrar ayuda');
                                                }}
                                            >
                                                <div className={`p-1.5 rounded-lg ${
                                                    active ? 'bg-success-content/20' : 'bg-base-300'
                                                }`}>
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                </div>
                                                <span className="flex-1 text-left">Ayuda y soporte</span>
                                            </button>
                                        )}
                                    </Menu.Item>
                                </div>

                                {/* Footer del menú */}
                                <div className="px-4 py-2 bg-base-200/50 border-t border-base-300">
                                    <p className="text-xs text-base-content/60 text-center">
                                        v1.0.0 • Configuración
                                    </p>
                                </div>
                            </Menu.Items>
                        </Transition>
                    </Menu>                </div>
            </div>
        </motion.div>
    );
};

export default HomeNavbar;
